import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Form, Input, Button, message, Steps, Row, Col,
  Divider, Typography, Space, Modal, Alert, Spin, Select,
  Tag, Popconfirm, Table, Tabs
} from 'antd';
import {
  PlusOutlined, SettingOutlined, PlayCircleOutlined,
  CheckCircleOutlined, SearchOutlined, EditOutlined, 
  DeleteOutlined, QrcodeOutlined, EnvironmentOutlined,
  UserOutlined, TeamOutlined, PictureOutlined
} from '@ant-design/icons';
import MessagePreview from '../components/sop/MessagePreview';
import AttachmentManager from '../components/sop/AttachmentManager';
import MaterialSelector from '../components/MaterialSelector';
import MaterialUploadModal from '../components/MaterialUploadModal';
import LbsPreview from '../components/LbsPreview';
import { useAuth } from '../utils/auth';
import { useNavigate, useParams } from 'react-router-dom';
import batchTaskService from '../services/batchTaskService';
import lbsContactWayService from '../services/lbsContactWayService';
import StoreConfigTable from '../components/batch/StoreConfigTable';
import StoreGroupConfigTable from '../components/batch/StoreGroupConfigTable';
import BatchTaskProgress from '../components/batch/BatchTaskProgress';
import fileService from '../services/fileService';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { Option } = Select;

/**
 * 批量活码创建页面
 */
const BatchContactWayCreation = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // 获取URL参数中的ID
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [storeConfigs, setStoreConfigs] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [taskProgress, setTaskProgress] = useState(null);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [lbsContactWayName, setLbsContactWayName] = useState('');
  
  // 编辑模式相关状态
  const [editMode, setEditMode] = useState(false);
  const [existingStores, setExistingStores] = useState([]);
  const [lbsContactWayData, setLbsContactWayData] = useState(null);
  // 编辑模式下：删除关联改为即时调用后端，不再使用暂存删除列表
  
  // 海报相关状态
  const [materialSelectorVisible, setMaterialSelectorVisible] = useState(false);
  const [selectedPosterMaterial, setSelectedPosterMaterial] = useState(null);
  const [originalPosterUrl, setOriginalPosterUrl] = useState(null);
  const posterChanged = useMemo(() => {
    const cur = selectedPosterMaterial?.previewUrl || null;
    const orig = originalPosterUrl || null;
    return cur !== orig;
  }, [selectedPosterMaterial, originalPosterUrl]);

  // 附件管理相关状态
  const [attachments, setAttachments] = useState([
    { id: 'text_1', type: 'TEXT', content: '' }
  ]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [nextAttachmentId, setNextAttachmentId] = useState(2);
  const [previewStep, setPreviewStep] = useState(null);

  // 标签相关状态
  const [tagMap, setTagMap] = useState(new Map());

  // 活码类型状态
  const [contactWayType, setContactWayType] = useState('MEMBER'); // 'MEMBER' | 'GROUP'
  
  // 初始化编辑模式检测
  useEffect(() => {
    if (id) {
      setEditMode(true);
      loadEditDetails();
    }
  }, [id]);
  
  // 加载编辑详情
  const loadEditDetails = async () => {
    try {
      setLoading(true);
      const response = await lbsContactWayService.getLbsContactWayEditDetails(id);
      
      // 直接使用axios响应的data字段，后端返回的就是编辑详情数据
      const editDetails = response.data;
      setLbsContactWayData(editDetails.lbsContactWay);
      setExistingStores(editDetails.existingStores || []);
      setLbsContactWayName(editDetails.lbsContactWay.name || '');
      // 初始化海报（编辑模式允许修改海报）
      const posterUrl = editDetails?.lbsContactWay?.posterPreviewUrl;
      setOriginalPosterUrl(posterUrl || null);
      if (posterUrl) {
        setSelectedPosterMaterial({ id: null, name: '已设置海报', previewUrl: posterUrl });
      }
      
      // 根据现有门店推断活码类型
      if (editDetails.existingStores && editDetails.existingStores.length > 0) {
        const firstStore = editDetails.existingStores[0];
        const detectedType = firstStore.contactWayType === 'MEMBER_CONTACT_WAY' ? 'MEMBER' : 'GROUP';
        setContactWayType(detectedType);
      }
      
      // message.success('编辑数据加载成功');
    } catch (error) {
      console.error('加载编辑数据失败:', error);
      message.error('加载编辑数据失败: ' + (error.message || '未知错误'));
      navigate('/lbs-contact-ways');
    } finally {
      setLoading(false);
    }
  };
  
  // 编辑模式：即时删除关联
  const handleDeleteExistingRelation = async (relationId) => {
    try {
      setLoading(true);
      await lbsContactWayService.deleteLbsContactWayRelation(relationId);
      setExistingStores(prev => prev.filter(s => s.relationId !== relationId));
      message.success('已删除门店关联');
    } catch (error) {
      console.error('删除关联失败:', error);
      message.error('删除关联失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 编辑模式：确认新增门店（直接创建并关联到现有LBS）
  const handleConfirmAddStores = async () => {
    try {
      if (!id) {
        message.error('缺少LBS活码ID');
        return;
      }
      if (storeConfigs.length === 0) {
        message.warning('请先选择并配置要新增的门店');
        return;
      }

      // 基础校验
      if (contactWayType === 'GROUP') {
        for (let i = 0; i < storeConfigs.length; i++) {
          const config = storeConfigs[i];
          if (!config.contactWayName || config.contactWayName.trim() === '') {
            message.error(`门店 ${config.storeName} 的群活码名称不能为空`);
            return;
          }
          if (!config.chatIdList || config.chatIdList.length === 0) {
            message.error(`门店 ${config.storeName} 必须选择至少一个群聊`);
            return;
          }
        }
      }

      setLoading(true);

      let request, response;
      if (contactWayType === 'MEMBER') {
        request = {
          existingLbsContactWayId: Number(id),
          stores: storeConfigs.map(config => ({
            storeId: config.storeId,
            contactWayName: config.contactWayName,
            userIds: (config.userIds || []).map(String),
            tags: (config.tags || []).map(String),
            remark: config.remark,
            skipVerify: config.skipVerify !== undefined ? config.skipVerify : true,
            state: config.state || generateStateParam()
          }))
        };
        response = await batchTaskService.appendMemberContactWaysToLbs(request);
      } else {
        request = {
          existingLbsContactWayId: Number(id),
          stores: storeConfigs.map(config => ({
            storeId: config.storeId,
            contactWayName: config.contactWayName,
            chatIds: (config.chatIdList || []).map(String),
            autoCreateRoom: config.autoCreateRoom !== undefined ? config.autoCreateRoom : true,
            roomBaseName: config.roomBaseName || '',
            roomBaseId: config.roomBaseId || 1,
            state: config.state || generateStateParam(),
            remark: config.remark || ''
          }))
        };
        response = await batchTaskService.appendGroupJoinWaysToLbs(request);
      }

      if (response.success) {
        setCurrentTask({
          taskId: response.taskId,
          taskName: response.taskName,
          totalCount: response.totalCount
        });
        setProgressModalVisible(true);
        message.success('已提交新增任务，后台正在执行');
        // 清空待添加配置，并刷新上方现有关联门店
        setStoreConfigs([]);
        await loadEditDetails();
      }
    } catch (error) {
      console.error('提交新增任务失败:', error);
      message.error('提交新增失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };


  // 步骤定义 - 根据活码类型和编辑模式动态调整
  const steps = editMode ? [
    {
      title: '活码管理',
      description: `管理已有关联（可直接删除）并新增门店配置（确认后直接新增）`,
      icon: <SettingOutlined />
    },
    {
      title: '海报设置',
      description: '单独修改该LBS活码海报并立即保存',
      icon: <PictureOutlined />
    }
  ] : [
    {
      title: '选择类型',
      description: `选择活码类型并配置${contactWayType === 'MEMBER' ? '成员' : '群'}活码参数`,
      icon: <SettingOutlined />
    },
    {
      title: '公共设置',
      description: contactWayType === 'MEMBER' 
        ? '配置成员活码的公共设置（自动通过好友、欢迎语）'
        : '群活码不需要公共设置',
      icon: <PlusOutlined />
    },
    {
      title: '海报设置',
      description: '为LBS活码选择展示海报（小程序显示）',
      icon: <PictureOutlined />
    },
    {
      title: '确认执行',
      description: '确认配置并开始批量创建',
      icon: <PlayCircleOutlined />
    },
    {
      title: '执行完成',
      description: '批量创建任务完成',
      icon: <CheckCircleOutlined />
    }
  ];

  // 计算已关联门店ID（用于在新增门店选择时禁选）
  const disabledStoreIds = useMemo(() => {
    const ids = (existingStores || [])
      .map(s => s.storeId)
      .filter(id => id != null);
    return Array.from(new Set(ids));
  }, [existingStores]);

  // 定时刷新任务进度
  useEffect(() => {
    if (currentTask?.taskId) {
      const fetchProgress = async () => {
        try {
          const response = await batchTaskService.getBatchTaskProgress(currentTask.taskId);
          if (response.success) {
            setTaskProgress(response.data);
            
            // 如果任务完成，停止轮询；编辑模式下延迟刷新现有关联门店并回到“活码管理”页签
            if (response.data.status === 'COMPLETED' || response.data.status === 'FAILED') {
              if (editMode) {
                setCurrentStep(0);
                // 等待后端事务/索引一致性
                await new Promise(resolve => setTimeout(resolve, 1500));
                await loadEditDetails();
              } else {
                setCurrentStep(4);
              }
              return false; // 停止轮询
            }
          }
        } catch (error) {
          console.error('获取任务进度失败:', error);
        }
        return true; // 继续轮询
      };

      // 立即获取一次进度
      fetchProgress();

      // 设置定时轮询（每3秒刷新一次）
      const interval = setInterval(async () => {
        const shouldContinue = await fetchProgress();
        if (!shouldContinue) {
          clearInterval(interval);
        }
      }, 3000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [currentTask?.taskId]);

  // 处理文件上传
  const handleFileUpload = async ({ file, onSuccess, onError, onProgress, fieldKey, mediaType, useTempMedia = true, stepType = 'MESSAGE' }) => {
    try {
      // 更新上传状态
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true, [fieldKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

      // 显示上传进度
      const updateProgress = (percent) => {
        setUploadProgress(prev => ({ ...prev, [uploadKey]: percent, [fieldKey]: percent }));
        if (onProgress) {
          onProgress({ percent });
        }
      };

      // 从localStorage获取租户信息
      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        if (onError) onError(new Error('未获取到租户信息'));
        return;
      }
      const tenant = JSON.parse(tenantStr);
      const tenantId = tenant.id;

      // 使用临时素材上传
      const result = await fileService.uploadMedia(file, mediaType, tenantId, updateProgress);

      if (result.success && result.mediaId) {
        console.log(`媒体上传成功 - 字段: ${fieldKey}, mediaId: ${result.mediaId}, ossUrl: ${result.ossUrl}`);

        // 添加预览URL到预览对象
        setPreviewUrls(prev => ({
          ...prev,
          [result.mediaId]: result.ossUrl
        }));

        message.success(`${file.name} 上传成功`);

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } else {
        const errorMsg = result?.message || '上传失败';
        message.error(errorMsg);
        if (onError) onError(new Error(errorMsg));
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      message.error(`${file.name} 上传失败: ${error.message}`);
      if (onError) onError(error);
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    }
  };

  // 处理文件移除
  const handleRemoveFile = (fieldKey, stepType = 'MESSAGE') => {
    console.log(`移除文件 - 字段: ${fieldKey}, 步骤类型: ${stepType}`);
    // 清除预览URL
    const attachmentToUpdate = attachments.find(att => att.id === fieldKey);
    if (attachmentToUpdate && attachmentToUpdate.mediaId) {
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[attachmentToUpdate.mediaId];
        return newUrls;
      });
    }
  };

  // 添加附件
  const handleAddAttachment = (type) => {
    const id = `attachment_${nextAttachmentId}`;
    const newAttachment = {
      id,
      type: type,
      content: '',
      // 根据类型初始化相关字段
      ...(type === 'IMAGE_GROUP' && { imageGroup: { mediaIds: [], ossUrls: {} } }),
      ...(type === 'VIDEO' && { mediaId: '', ossUrl: '' }),
      ...(type === 'LINK' && { title: '', description: '', url: '' }),
      ...(type === 'MINIPROGRAM' && { title: '', appid: '', pagepath: '', thumbMediaId: '', thumbMediaId_ossUrl: '' }),
      ...(type === 'FILE' && { mediaId: '', ossUrl: '' })
    };

    setAttachments(prev => [...prev, newAttachment]);
    setNextAttachmentId(prev => prev + 1);
    return id;
  };

  // 移除附件
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));

    // 清理相关的预览URL
    const attachmentToRemove = attachments.find(att => att.id === attachmentId);
    if (attachmentToRemove) {
      if (attachmentToRemove.mediaId) {
        setPreviewUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[attachmentToRemove.mediaId];
          return newUrls;
        });
      }
      if (attachmentToRemove.imageGroup && attachmentToRemove.imageGroup.mediaIds) {
        setPreviewUrls(prev => {
          const newUrls = { ...prev };
          attachmentToRemove.imageGroup.mediaIds.forEach(mediaId => {
            delete newUrls[mediaId];
          });
          return newUrls;
        });
      }
    }
  };

  // 更新附件内容
  const handleUpdateAttachmentContent = (attachmentId, field, value) => {
    setAttachments(prev => prev.map(att =>
      att.id === attachmentId ? { ...att, [field]: value } : att
    ));
  };

  // 插入客户昵称
  const insertNickname = () => {
    setAttachments(prev => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex(a => a && a.type === 'TEXT');
      if (idx === -1) return [{ id: `text-${Date.now()}`, type: 'TEXT', content: '%NICKNAME%' }, ...list];
      const target = list[idx];
      const before = target.content || '';
      list[idx] = { ...target, content: `${before}%NICKNAME%` };
      return list;
    });
  };

  // 更新预览
  const updatePreview = () => {
    const textAttachment = attachments.find(att => att.type === 'TEXT');
    const otherAttachments = attachments.filter(att => att.type !== 'TEXT');

    // 构建符合MessagePreview期望格式的数据
    const attachmentList = [];

    // 先添加文本（如果有的话）
    if (textAttachment?.content) {
      attachmentList.push({
        msgtype: 'text',
        text: {
          content: textAttachment.content
        }
      });
    }

    // 再添加其他附件
    otherAttachments.forEach(att => {
      if (att.type === 'IMAGE_GROUP' && att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0) {
        // 为图片组中的每张图片创建单独的图片附件
        att.imageGroup.mediaIds.forEach(mediaId => {
          attachmentList.push({
            msgtype: 'image',
            image: {
              media_id: mediaId,
              oss_url: att.imageGroup.ossUrls[mediaId] || ''
            }
          });
        });
      } else if (att.type === 'VIDEO' && att.mediaId) {
        attachmentList.push({
          msgtype: 'video',
          video: {
            media_id: att.mediaId,
            oss_url: att.ossUrl || ''
          }
        });
      } else if (att.type === 'LINK' && att.url) {
        attachmentList.push({
          msgtype: 'link',
          link: {
            title: att.title || '',
            desc: att.description || '',
            url: att.url,
            picurl: '',
            pic_oss_url: ''
          }
        });
      } else if (att.type === 'MINIPROGRAM' && att.appid && att.title) {
        attachmentList.push({
          msgtype: 'miniprogram',
          miniprogram: {
            title: att.title,
            appid: att.appid,
            page: att.pagepath || '',
            pic_media_id: att.thumbMediaId || '',
            pic_oss_url: att.thumbMediaId_ossUrl || ''
          }
        });
      } else if (att.type === 'FILE' && att.mediaId) {
        attachmentList.push({
          msgtype: 'file',
          file: {
            media_id: att.mediaId,
            oss_url: att.ossUrl || ''
          }
        });
      }
    });

    const step = {
      actionType: 'SEND_MESSAGE',
      actionParams: {
        text: textAttachment?.content || '',
        attachments: attachmentList
      }
    };

    setPreviewStep(step);
  };

  // 监听附件变化以更新预览
  useEffect(() => {
    updatePreview();
  }, [attachments]);

  // 接收StoreConfigTable组件传递的标签映射
  const handleTagMapReady = (map) => {
    setTagMap(map);
  };

  /**
   * 处理表单提交
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (storeConfigs.length === 0) {
        message.error('请至少选择一个门店');
        return;
      }
      
      // 验证群活码配置
      if (contactWayType === 'GROUP') {
        for (let i = 0; i < storeConfigs.length; i++) {
          const config = storeConfigs[i];
          if (!config.contactWayName || config.contactWayName.trim() === '') {
            message.error(`门店 ${config.storeName} 的群活码名称不能为空`);
            return;
          }
          if (!config.chatIdList || config.chatIdList.length === 0) {
            message.error(`门店 ${config.storeName} 必须选择至少一个群聊`);
            return;
          }
        }
      }

      setLoading(true);

      // 构建欢迎语数据
      const textAttachment = attachments.find(att => att.type === 'TEXT');
      const otherAttachments = attachments.filter(att => att.type !== 'TEXT');

      const welcomeData = {
        text: textAttachment?.content || '',
        attachments: otherAttachments.map(att => {
          if (att.type === 'IMAGE_GROUP') {
            return {
              type: 'IMAGE_GROUP',
              imageGroup: att.imageGroup
            };
          } else if (att.type === 'VIDEO') {
            return {
              type: 'VIDEO',
              mediaId: att.mediaId,
              ossUrl: att.ossUrl
            };
          } else if (att.type === 'LINK') {
            return {
              type: 'LINK',
              title: att.title,
              description: att.description,
              url: att.url
            };
          } else if (att.type === 'MINIPROGRAM') {
            return {
              type: 'MINIPROGRAM',
              title: att.title,
              appid: att.appid,
              pagepath: att.pagepath,
              thumbMediaId: att.thumbMediaId,
              thumbMediaId_ossUrl: att.thumbMediaId_ossUrl
            };
          } else if (att.type === 'FILE') {
            return {
              type: 'FILE',
              mediaId: att.mediaId,
              ossUrl: att.ossUrl
            };
          }
          return att;
        }).filter(att => {
          // 过滤掉空的附件
          if (att.type === 'IMAGE_GROUP') {
            return att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0;
          } else if (att.type === 'VIDEO' || att.type === 'FILE') {
            return att.mediaId;
          } else if (att.type === 'LINK') {
            return att.url;
          } else if (att.type === 'MINIPROGRAM') {
            return att.appid && att.title;
          }
          return false;
        })
      };
      
      // 根据活码类型构建不同的批量创建请求
      let request, response;
      
      if (contactWayType === 'MEMBER') {
        // 成员活码批量创建请求
        request = {
          stores: storeConfigs.map(config => ({
            storeId: config.storeId,
            contactWayName: config.contactWayName,
            userIds: config.userIds || [],
            tags: config.tags || [],
            remark: config.remark,
            // 门店级别设置优先，否则使用公共设置
            skipVerify: config.skipVerify !== undefined ? config.skipVerify : values.skipVerify,
            // 系统自动设置
            isTemp: false, // 固定为false
            state: config.state || generateStateParam() // 使用已有的state或生成新的
          })),
          commonSettings: {
            skipVerify: values.skipVerify,
            welcomeMsg: welcomeData, // 公共欢迎语数据
            isTemp: false, // 固定为false
            isExclusive: false // 默认为false，表示非独占
          },
          // 添加LBS活码名称参数
          lbsContactWayName: lbsContactWayName.trim() || '默认LBS活码',
          lbsContactWayRemark: `成员活码批量创建 - ${storeConfigs.length}个门店`,
          // 添加海报信息（如果选择了海报）
          ...(selectedPosterMaterial && {
            posterMaterialId: selectedPosterMaterial.id,
            posterPreviewUrl: selectedPosterMaterial.previewUrl
          })
        };
        
        // 调用成员活码批量创建API
        response = await batchTaskService.createMemberContactWaysBatch(request);
        
      } else {
        // 群活码批量创建请求（不支持欢迎语）
        request = {
          stores: storeConfigs.map(config => ({
            storeId: config.storeId,
            contactWayName: config.contactWayName, // 群活码名称
            chatIds: config.chatIdList || [], // 群聊ID列表（注意字段名是chatIds）
            autoCreateRoom: config.autoCreateRoom !== undefined ? config.autoCreateRoom : true,
            roomBaseName: config.roomBaseName || '',
            roomBaseId: config.roomBaseId || 1,
            state: config.state || generateStateParam(),
            remark: config.remark || '' // 备注信息
          })),
          // 添加LBS活码名称参数
          lbsContactWayName: lbsContactWayName.trim() || '默认LBS活码',
          lbsContactWayRemark: `群活码批量创建 - ${storeConfigs.length}个门店`,
          // 添加海报信息（如果选择了海报）
          ...(selectedPosterMaterial && {
            posterMaterialId: selectedPosterMaterial.id,
            posterPreviewUrl: selectedPosterMaterial.previewUrl
          })
        };
        
        // 调用群活码批量创建API
        response = await batchTaskService.createGroupJoinWaysBatch(request);
      }
      
      if (response.success) {
        setCurrentTask({
          taskId: response.taskId,
          taskName: response.taskName,
          totalCount: response.totalCount
        });
        
        // 显示进度监控弹窗
        setProgressModalVisible(true);
        setCurrentStep(4);
        
        message.success('批量任务创建成功，正在后台执行...');
      }

    } catch (error) {
      console.error('创建批量任务失败:', error);
      message.error('创建失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 生成唯一的状态参数
   */
  const generateStateParam = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `tw_lbs_${timestamp}_${random}`;
  };

  /**
   * 生成欢迎语数据
   */
  const generateWelcomeData = () => {
    const textAttachment = attachments.find(att => att.type === 'TEXT');
    const otherAttachments = attachments.filter(att => att.type !== 'TEXT');

    return {
      text: textAttachment?.content || '',
      attachments: otherAttachments.map(att => {
        if (att.type === 'IMAGE_GROUP') {
          return {
            type: 'IMAGE_GROUP',
            imageGroup: att.imageGroup
          };
        } else if (att.type === 'VIDEO') {
          return {
            type: 'VIDEO',
            mediaId: att.mediaId,
            ossUrl: att.ossUrl
          };
        } else if (att.type === 'LINK') {
          return {
            type: 'LINK',
            title: att.title,
            description: att.description,
            url: att.url
          };
        } else if (att.type === 'MINIPROGRAM') {
          return {
            type: 'MINIPROGRAM',
            title: att.title,
            appid: att.appid,
            pagepath: att.pagepath,
            thumbMediaId: att.thumbMediaId,
            thumbMediaId_ossUrl: att.thumbMediaId_ossUrl
          };
        } else if (att.type === 'FILE') {
          return {
            type: 'FILE',
            mediaId: att.mediaId,
            ossUrl: att.ossUrl
          };
        }
        return att;
      }).filter(att => {
        // 过滤掉空的附件
        if (att.type === 'IMAGE_GROUP') {
          return att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0;
        } else if (att.type === 'VIDEO' || att.type === 'FILE') {
          return att.mediaId;
        } else if (att.type === 'LINK') {
          return att.url;
        } else if (att.type === 'MINIPROGRAM') {
          return att.appid && att.title;
        }
        return false;
      })
    };
  };

  /**
   * 下一步
   */
  const handleNext = () => {
    if (currentStep === 0) {
      if (!lbsContactWayName.trim()) {
        message.error('请输入LBS活码名称');
        return;
      }
      
      // 只在创建模式下验证门店选择，编辑模式允许自由浏览步骤
      if (!editMode && storeConfigs.length === 0) {
        message.error('请至少选择一个门店');
        return;
      }
    }
    
    // 在进入确认配置步骤前，为每个门店生成独立的state参数（用户不可见）
    if (currentStep === 2) {
      const updatedConfigs = storeConfigs.map(config => ({
        ...config,
        state: config.state || generateStateParam() // 如果还没有state则生成，否则保持原有
      }));
      setStoreConfigs(updatedConfigs);
    }
    
    setCurrentStep(currentStep + 1);
  };

  /**
   * 上一步
   */
  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  /**
   * 处理海报素材选择
   */
  const handlePosterMaterialSelect = (material) => {
    if (material && material.materialType === 'IMAGE') {
      // 海报只支持图片类型，使用MaterialSelector已转换好的previewUrl
      const previewUrl = material.imageInfo?.previewUrl || material.imageInfo?.url;
      
      console.log('Selected poster material:', material, 'previewUrl:', previewUrl);
      
      setSelectedPosterMaterial({
        id: material.id,
        name: material.name,
        previewUrl: previewUrl
      });
      setMaterialSelectorVisible(false);
    }
  };

  // 素材上传（用于在素材选择器内直接上传）
  const [materialUploadVisible, setMaterialUploadVisible] = useState(false);
  const [uploadMaterialType, setUploadMaterialType] = useState('IMAGE');
  const [materialRefreshTrigger, setMaterialRefreshTrigger] = useState(0);

  /**
   * 获取租户ID
   */
  const getTenantId = () => {
    const tenantStr = localStorage.getItem('tenant');
    if (tenantStr) {
      const tenant = JSON.parse(tenantStr);
      return tenant.id;
    }
    return null;
  };

  /**
   * 重置表单
   */
  const handleReset = () => {
    form.resetFields();
    setStoreConfigs([]);
    setCurrentStep(0);
    setCurrentTask(null);
    setTaskProgress(null);
    setProgressModalVisible(false);
  };

  /**
   * 重试失败任务
   */
  const handleRetryFailed = async () => {
    if (!currentTask?.taskId) return;
    
    try {
      const response = await batchTaskService.retryFailedItems(currentTask.taskId);
      if (response.success) {
        message.success(`已重试 ${response.retriedCount} 个失败任务`);
      }
    } catch (error) {
      message.error('重试失败: ' + error.message);
    }
  };


  /**
   * 渲染步骤内容
   */
  const renderStepContent = () => {
    // 编辑模式下的步骤内容
    if (editMode) {
      return renderEditStepContent();
    }
    
    // 创建模式下的步骤内容
    switch (currentStep) {
      case 0:
        return (
          <Card title="选择活码类型并配置参数">
            {/* 活码类型选择 */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f8ff' }}>
              <Row gutter={16} align="middle">
                <Col span={4}>
                  <Text strong>活码类型:</Text>
                </Col>
                <Col span={12}>
                  <Select
                    value={contactWayType}
                    onChange={(value) => {
                      setContactWayType(value);
                      // 切换类型时清空门店配置
                      setStoreConfigs([]);
                    }}
                    style={{ width: '100%' }}
                    size="middle"
                  >
                    <Option value="MEMBER">
                      <Space>
                        <UserOutlined />
                        成员活码
                      </Space>
                    </Option>
                    <Option value="GROUP">
                      <Space>
                        <TeamOutlined />
                        群活码
                      </Space>
                    </Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <Text type="secondary">
                    {contactWayType === 'MEMBER' 
                      ? '创建成员活码，用于添加具体的企业成员'
                      : '创建群活码，用于邀请用户加入群聊'
                    }
                  </Text>
                </Col>
              </Row>
            </Card>
            
            {/* LBS活码名称设置 */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
              <Row gutter={16} align="middle">
                <Col span={4}>
                  <Text strong>LBS活码名称:</Text>
                </Col>
                <Col span={12}>
                  <Input
                    placeholder={
                      contactWayType === 'MEMBER' 
                        ? '请输入LBS活码名称，将作为门店成员活码名称的前缀'
                        : '请输入LBS活码名称，将作为门店群活码名称的前缀'
                    }
                    value={lbsContactWayName}
                    onChange={(e) => setLbsContactWayName(e.target.value)}
                    maxLength={20}
                    showCount
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary">
                    例如：输入"夏季促销"，门店活码名称将自动变为"夏季促销-门店名称"
                  </Text>
                </Col>
              </Row>
            </Card>
            
            {/* 根据活码类型显示不同的配置组件 */}
            {contactWayType === 'MEMBER' ? (
              <StoreConfigTable
                value={storeConfigs}
                onChange={setStoreConfigs}
                loading={loading}
                lbsContactWayName={lbsContactWayName}
                onTagMapReady={handleTagMapReady}
                disabledStoreIds={disabledStoreIds}
              />
            ) : (
              <StoreGroupConfigTable
                value={storeConfigs}
                onChange={setStoreConfigs}
                loading={loading}
                lbsContactWayName={lbsContactWayName}
                disabledStoreIds={disabledStoreIds}
              />
            )}
            
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Button 
                type="primary" 
                onClick={handleNext}
                disabled={storeConfigs.length === 0}
              >
                下一步
              </Button>
            </div>
          </Card>
        );

      case 1:
        return (
          <Card title="公共设置">
            {contactWayType === 'MEMBER' ? (
              // 成员活码支持欢迎语和自动通过好友设置
              <Form form={form} layout="vertical">
                <Form.Item
                  name="skipVerify"
                  label="自动通过好友"
                  initialValue={true}
                >
                  <Select placeholder="选择是否自动通过好友">
                    <Select.Option value={true}>是</Select.Option>
                    <Select.Option value={false}>否</Select.Option>
                  </Select>
                </Form.Item>

                <Divider />

                {/* 欢迎语设置 */}
                <Title level={5} style={{ marginBottom: '24px' }}>欢迎语设置</Title>
              <Row gutter={24}>
                <Col span={14}>
                  <Text strong>编辑欢迎语内容</Text>
                  <Button
                    size="small"
                    onClick={insertNickname}
                    style={{ marginLeft: '12px' }}
                  >
                    插入客户昵称 (%NICKNAME%)
                  </Button>
                  <AttachmentManager
                    attachments={attachments}
                    onUpdateContent={handleUpdateAttachmentContent}
                    onAddAttachment={handleAddAttachment}
                    onRemoveAttachment={handleRemoveAttachment}
                    previewUrls={previewUrls}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    handleFileUpload={handleFileUpload}
                    handleRemoveFile={handleRemoveFile}
                    stepType="MESSAGE"
                    isTemplateActive={false}
                  />
                </Col>

                <Col span={10}>
                  <div style={{ marginTop: '8px' }}>
                    {previewStep ? (
                      <MessagePreview
                        key={JSON.stringify(previewStep)}
                        step={{
                          type: 'MESSAGE',
                          actionType: 'SEND_MESSAGE',
                          messageList: attachments.map(attachment => {
                            if (attachment.type === 'TEXT') {
                              return {
                                type: 'TEXT',
                                params: { content: attachment.content || '' }
                              };
                            } else if (attachment.type === 'IMAGE_GROUP') {
                              return {
                                type: 'IMAGE_GROUP',
                                params: {
                                  imageGroup: attachment.imageGroup || { mediaIds: [], ossUrls: {} }
                                }
                              };
                            } else if (attachment.type === 'VIDEO') {
                              return {
                                type: 'VIDEO',
                                params: {
                                  mediaId: attachment.mediaId || '',
                                  ossUrl: attachment.ossUrl || ''
                                }
                              };
                            } else if (attachment.type === 'LINK') {
                              return {
                                type: 'LINK',
                                params: {
                                  title: attachment.title || '',
                                  url: attachment.url || '',
                                  description: attachment.description || '',
                                  picUrl: attachment.picUrl || '',
                                  picUrl_ossUrl: attachment.picUrl_ossUrl || ''
                                }
                              };
                            } else if (attachment.type === 'MINIPROGRAM') {
                              return {
                                type: 'MINIPROGRAM',
                                params: {
                                  title: attachment.title || '',
                                  appid: attachment.appid || '',
                                  pagepath: attachment.pagepath || '',
                                  thumbMediaId: attachment.thumbMediaId || '',
                                  thumbMediaId_ossUrl: attachment.thumbMediaId_ossUrl || ''
                                }
                              };
                            } else if (attachment.type === 'FILE') {
                              return {
                                type: 'FILE',
                                params: {
                                  mediaId: attachment.mediaId || '',
                                  ossUrl: attachment.ossUrl || ''
                                }
                              };
                            }
                            return null;
                          }).filter(Boolean)
                        }}
                        noTruncate={true}
                      />
                    ) : (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        color: '#999',
                        border: '1px dashed #d9d9d9',
                        borderRadius: '6px'
                      }}>
                        <Text type="secondary">请编辑欢迎语内容以查看预览效果</Text>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

                <Alert
                  message="提示"
                  description="这些设置将作为所有活码的默认配置，单个活码的设置可以覆盖这些公共设置。"
                  type="info"
                  style={{ marginTop: 16 }}
                />
              </Form>
            ) : (
              // 群活码不支持欢迎语和自动通过好友设置
              <div style={{ padding: '40px', textAlign: 'center', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
                <TeamOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '16px' }}>群活码不需要公共设置</Text>
                </div>
                <Text type="secondary">群活码不支持欢迎语设置，所有配置在第一步中完成</Text>
              </div>
            )}

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={handlePrevious}>
                  上一步
                </Button>
                <Button type="primary" onClick={handleNext}>
                  下一步
                </Button>
              </Space>
            </div>
          </Card>
        );

      case 2:
        return (
          <Card title="海报设置">
            <Alert
              message="海报设置（可选）"
              description="为LBS活码选择海报图片，将在小程序页面顶部展示。不选择则不显示海报。"
              type="info"
              style={{ marginBottom: 16 }}
            />

            <Row gutter={24} align="top">
              {/* 左侧：海报选择区 */}
              <Col span={14}>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>当前海报：</Text>
                    {selectedPosterMaterial?.previewUrl ? (
                      <div style={{ 
                        marginTop: 8, 
                        padding: 16, 
                        border: '1px dashed #d9d9d9', 
                        borderRadius: 6,
                        textAlign: 'center',
                        minHeight: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <img
                          src={selectedPosterMaterial.previewUrl}
                          alt="海报预览"
                          style={{ maxHeight: 360, maxWidth: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    ) : (
                      <div style={{ 
                        marginTop: 8, 
                        padding: 16,
                        minHeight: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#8c8c8c',
                        fontStyle: 'italic'
                      }}>
                        未选择海报
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 'auto' }}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<PictureOutlined />}
                        onClick={() => setMaterialSelectorVisible(true)}
                      >
                        选择海报
                      </Button>
                      
                      {selectedPosterMaterial && (
                        <Button
                          onClick={() => setSelectedPosterMaterial(null)}
                        >
                          移除海报
                        </Button>
                      )}
                    </Space>
                  </div>
                </div>
              </Col>

              {/* 右侧：手机预览区 */}
              <Col span={10}>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16, width: '100%' }}>
                    <Text strong style={{ marginBottom: 0, display: 'inline-block' }}>手机端预览</Text>
                    <Text type="secondary" style={{ marginBottom: 0 }}>建议海报尺寸：750×1624</Text>
                  </div>
                  <LbsPreview
                    posterUrl={selectedPosterMaterial?.previewUrl}
                    qrCodeValue="https://work.weixin.qq.com/ca/placeholder"
                    lbsName={lbsContactWayName || 'LBS活码'}
                    width={320}
                    height={693}
                  />
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Space>
                <Button onClick={handlePrevious}>
                  上一步
                </Button>
                <Button type="primary" onClick={handleNext}>
                  下一步
                </Button>
              </Space>
            </div>
          </Card>
        );

      case 3:
        return (
          <Card title="确认配置">
            <Alert
              message="即将开始批量创建"
              description={`将为 ${storeConfigs.length} 个门店批量创建${contactWayType === 'MEMBER' ? '成员' : '群'}活码，请确认配置无误后点击开始执行。`}
              type="warning"
              style={{ marginBottom: 24 }}
            />

            <Title level={4}>门店列表</Title>
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 16 }}>
              {storeConfigs.map((config, index) => (
                <Card 
                  key={config.storeId}
                  size="small" 
                  style={{ marginBottom: 8 }}
                  title={`${index + 1}. ${config.storeName}`}
                >
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Text strong>活码名称：</Text>{config.contactWayName}
                    </Col>
                    <Col span={12}>
                      <Text strong>
                        {contactWayType === 'MEMBER' ? '成员数量：' : '群聊数量：'}
                      </Text>
                      {contactWayType === 'MEMBER' 
                        ? (config.userIds?.length || 0)
                        : (config.chatIdList?.length || 0)
                      }
                    </Col>
                    {/* 成员活码显示标签，群活码显示自动创建群设置 */}
                    {contactWayType === 'MEMBER' && config.tags && config.tags.length > 0 && (
                      <Col span={24}>
                        <Text strong>标签：</Text>
                        {config.tags.map(tagId => {
                          const tagName = tagMap.get(parseInt(tagId)) || `ID:${tagId}`;
                          return (
                            <Tag key={tagId} color="blue" style={{ marginRight: 4 }}>
                              {tagName}
                            </Tag>
                          );
                        })}
                      </Col>
                    )}
                    
                    {contactWayType === 'GROUP' && config.autoCreateRoom && (
                      <Col span={24}>
                        <Text strong>自动创建群：</Text>
                        <Tag color="green">是</Tag>
                        {config.roomBaseName && (
                          <Text type="secondary">（基础名称：{config.roomBaseName}）</Text>
                        )}
                      </Col>
                    )}
                  </Row>
                </Card>
              ))}
            </div>

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={handlePrevious}>
                  上一步
                </Button>
                <Button 
                  type="primary" 
                  loading={loading}
                  onClick={handleSubmit}
                >
                  开始执行
                </Button>
              </Space>
            </div>
          </Card>
        );

      case 4:
        return (
          <Card title="执行完成">
            {currentTask && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                {taskProgress?.status === 'COMPLETED' && (
                  <div>
                    <CheckCircleOutlined 
                      style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} 
                    />
                    <Title level={3} style={{ color: '#52c41a' }}>
                      批量创建完成！
                    </Title>
                    <Paragraph>
                      成功为 {taskProgress.successCount} 个门店创建了{contactWayType === 'MEMBER' ? '成员' : '群'}活码
                      {taskProgress.failedCount > 0 && 
                        `，其中 ${taskProgress.failedCount} 个失败`}
                    </Paragraph>
                    <Paragraph type="secondary">
                      所有创建的活码都可以在 LBS活码管理页面中查看和管理。
                    </Paragraph>
                  </div>
                )}
                
                {taskProgress?.status === 'FAILED' && (
                  <div>
                    <Alert
                      message="批量创建失败"
                      description="部分或全部门店的活码创建失败，请查看详细信息并重试。"
                      type="error"
                      style={{ marginBottom: 16 }}
                    />
                  </div>
                )}

                <Space style={{ marginTop: 24 }}>
                  <Button onClick={() => setProgressModalVisible(true)}>
                    查看详情
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={() => navigate('/lbs-contact-ways')}
                  >
                    查看所有LBS活码
                  </Button>
                  {taskProgress?.failedCount > 0 && (
                    <Button onClick={handleRetryFailed}>
                      重试失败项
                    </Button>
                  )}
                  <Button onClick={handleReset}>
                    继续创建
                  </Button>
                </Space>
              </div>
            )}
          </Card>
        );

      default:
        return null;
    }
  };
  
  // 现有门店列表组件（支持即时删除）
  const ExistingStoresList = ({ existingStores, onDeleteRelation }) => {
    if (!existingStores || existingStores.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          <Text>暂无门店关联</Text>
        </div>
      );
    }
    
    const columns = [
      {
        title: '门店名称',
        dataIndex: 'storeName',
        key: 'storeName'
      },
      {
        title: '活码类型',
        dataIndex: 'contactWayType',
        key: 'contactWayType',
        render: (type) => (
          <Tag color={type === 'MEMBER_CONTACT_WAY' ? 'blue' : 'green'}>
            {type === 'MEMBER_CONTACT_WAY' ? '成员活码' : '群活码'}
          </Tag>
        )
      },
      {
        title: '活码名称',
        dataIndex: 'contactWayName',
        key: 'contactWayName',
        render: (name, record) => (
          <span style={{ color: record.contactWayDeleted ? '#ff4d4f' : 'inherit' }}>
            {name || '未知'}
            {record.contactWayDeleted && (
              <Tag color="red" style={{ marginLeft: 8 }}>已删除</Tag>
            )}
          </span>
        )
      },
      {
        title: '状态',
        dataIndex: 'contactWayStatus',
        key: 'contactWayStatus',
        render: (status) => {
          const color = status === 'active' ? 'success' : status === 'deleted' ? 'error' : 'default';
          const text = status === 'active' ? '正常' : status === 'deleted' ? '已删除' : '未知';
          return <Tag color={color}>{text}</Tag>;
        }
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Popconfirm
            title="确认删除门店关联吗？"
            description="仅删除关联关系，不会删除具体活码。如需删除活码，请前往活码管理页面。"
            onConfirm={() => onDeleteRelation(record.relationId)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除关联
            </Button>
          </Popconfirm>
        )
      }
    ];
    
    return (
      <Table
        columns={columns}
        dataSource={existingStores}
        rowKey="relationId"
        pagination={false}
        size="middle"
      />
    );
  };
  
  // 已精简流程：删除确认视图组件
  
  /**
   * 渲染编辑模式的步骤内容
   */
  const renderEditStepContent = () => {
    switch (currentStep) {
      case 0:
        // 活码管理：展示现有关联（可删除）+ 新增门店配置（确认后直接新增）
        return (
          <Card title="活码管理">
            <Alert
              message="编辑提示"
              description="已有关联的门店可直接删除。下方可新增门店配置，确认后直接提交新增任务。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <ExistingStoresList 
              existingStores={existingStores}
              onDeleteRelation={handleDeleteExistingRelation}
            />
            <Divider />
            <Alert
              message="新增门店"
              description="为该LBS活码新增门店关联。点击“确认新增”将直接创建对应活码并建立关联。"
              type="success"
              style={{ marginBottom: 16 }}
            />
            {contactWayType === 'MEMBER' ? (
              <StoreConfigTable
                value={storeConfigs}
                onChange={setStoreConfigs}
                loading={loading}
                lbsContactWayName={lbsContactWayName}
                onTagMapReady={handleTagMapReady}
                disabledStoreIds={disabledStoreIds}
              />
            ) : (
              <StoreGroupConfigTable
                value={storeConfigs}
                onChange={setStoreConfigs}
                loading={loading}
                lbsContactWayName={lbsContactWayName}
                disabledStoreIds={disabledStoreIds}
              />
            )}
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Space>
                <Button type="primary" disabled={storeConfigs.length === 0} onClick={handleConfirmAddStores} loading={loading}>
                  确认新增
                </Button>
              </Space>
            </div>
          </Card>
        );

      case 1:
        // 海报设置（可修改，单独保存）
        return (
          <Card title="海报设置">
            <Alert
              message="海报设置（可选）"
              description="为LBS活码选择海报图片，将在小程序页面顶部展示。不选择则不显示海报。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={24} align="top">
              <Col span={14}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>当前海报：</Text>
                    {selectedPosterMaterial?.previewUrl ? (
                      <div style={{ marginTop: 8, padding: 16, border: '1px dashed #d9d9d9', borderRadius: 6, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={selectedPosterMaterial.previewUrl} alt="海报预览" style={{ maxHeight: 360, maxWidth: '100%', objectFit: 'contain' }} />
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, padding: 16, minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontStyle: 'italic' }}>
                        未选择海报
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 'auto' }}>
                    <Space>
                      <Button type="primary" icon={<PictureOutlined />} onClick={() => setMaterialSelectorVisible(true)}>
                        选择海报
                      </Button>
                      {selectedPosterMaterial && (
                        <Button onClick={() => setSelectedPosterMaterial(null)}>移除海报</Button>
                      )}
                    </Space>
                  </div>
                </div>
              </Col>
              <Col span={10}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16, width: '100%' }}>
                    <Text strong style={{ marginBottom: 0, display: 'inline-block' }}>手机端预览</Text>
                    <Text type="secondary" style={{ marginBottom: 0 }}>建议海报尺寸：750×1624</Text>
                  </div>
                  <LbsPreview
                    posterUrl={selectedPosterMaterial?.previewUrl}
                    qrCodeValue="https://work.weixin.qq.com/ca/placeholder"
                    lbsName={lbsContactWayName || 'LBS活码'}
                    width={320}
                    height={693}
                  />
                </div>
              </Col>
            </Row>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Space>
                <Button type="primary" onClick={async () => {
                  try {
                    if (!id) {
                      message.error('缺少LBS活码ID');
                      return;
                    }
                    setLoading(true);
                    await lbsContactWayService.updateLbsContactWayPoster(id, {
                      posterMaterialId: selectedPosterMaterial?.id || null
                    });
                    setOriginalPosterUrl(selectedPosterMaterial?.previewUrl || null);
                    message.success('海报已保存');
                  } catch (e) {
                    console.error('更新海报失败:', e);
                    message.error('更新海报失败: ' + (e.message || '未知错误'));
                  } finally {
                    setLoading(false);
                  }
                }}>保存海报</Button>
              </Space>
            </div>
          </Card>
        );

      
      default:
        return null;
    }
  };


  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>
            {editMode ? 'LBS活码编辑' : 'LBS活码批量创建'}
            {editMode && lbsContactWayData && (
              <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#666', marginLeft: '16px' }}>
                - {lbsContactWayData.name}
              </span>
            )}
          </Title>
          <Paragraph>
            {editMode ? (
              <>
                编辑LBS活码的门店关联关系，您可以新增或删除门店。
                <Text type="warning" style={{ marginLeft: 8 }}>
                  如需编辑活码具体内容，请前往活码管理页面操作。
                </Text>
              </>
            ) : (
              `为多个门店批量创建${contactWayType === 'MEMBER' ? '成员' : '群'}活码，支持个性化配置每个门店的活码参数。`
            )}
          </Paragraph>
        </div>

        {editMode ? (
          <Tabs
            activeKey={String(currentStep)}
            onChange={(key) => setCurrentStep(Number(key))}
            items={[
              { key: '0', label: '活码管理' },
              { key: '1', label: '海报设置' }
            ]}
          />
        ) : (
          <Steps current={currentStep} style={{ marginBottom: 32 }}>
            {steps.map((step, index) => (
              <Step 
                key={index}
                title={step.title}
                description={step.description}
                icon={step.icon}
              />
            ))}
          </Steps>
        )}

        <Spin spinning={loading}>
          {renderStepContent()}
        </Spin>
      </Card>

      {/* 海报素材选择器 */}
      <MaterialSelector
        visible={materialSelectorVisible}
        onCancel={() => setMaterialSelectorVisible(false)}
        onSelect={handlePosterMaterialSelect}
        materialType="IMAGE" // 只允许选择图片
        multiSelect={false} // 单选
        title="选择海报素材"
        allowUpload={true}
        onUpload={(selectedMaterialType) => {
          setUploadMaterialType(selectedMaterialType || 'IMAGE');
          setMaterialUploadVisible(true);
        }}
        refreshTrigger={materialRefreshTrigger}
        tenantId={getTenantId()}
      />

      {/* 素材上传Modal */}
      <MaterialUploadModal
        visible={materialUploadVisible}
        onCancel={() => setMaterialUploadVisible(false)}
        onSuccess={() => {
          // 上传成功后关闭Modal并刷新素材列表
          setMaterialUploadVisible(false);
          setMaterialRefreshTrigger(prev => prev + 1);
        }}
        initialType={uploadMaterialType}
        hideTypeSelection={true}
        tenantId={getTenantId()}
      />

      {/* 进度监控弹窗 */}
      <Modal
        title="批量创建进度"
        open={progressModalVisible}
        onCancel={() => setProgressModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setProgressModalVisible(false)}>
            关闭
          </Button>,
          taskProgress?.failedCount > 0 && (
            <Button key="retry" type="primary" onClick={handleRetryFailed}>
              重试失败项
            </Button>
          )
        ]}
        width={800}
      >
        {currentTask && (
          <BatchTaskProgress
            task={currentTask}
            progress={taskProgress}
            onRetryFailed={handleRetryFailed}
          />
        )}
      </Modal>
    </div>
  );
};

export default BatchContactWayCreation;
